import { useEffect, useState } from 'react';
import WebApp from '@twa-dev/sdk';
import { RestTimer } from './skills/RestTimer.js';
import { TwelveWeekTracker } from './skills/TwelveWeekTracker.js';
import { PRChallenge } from './skills/PRChallenge.js';
import { FormGuide } from './skills/FormGuide.js';
import { InjuryRecovery } from './skills/InjuryRecovery.js';
import { SkillView } from './skills/SkillView.js';
import { fetchMe, type MeResponse } from './lib/api.js';

type Screen =
  | 'home'
  | 'rest_timer'
  | 'tracker'
  | 'pr'
  | 'form'
  | 'injury'
  | 'briefing'
  | 'meal'
  | 'recovery'
  | 'countdown'
  | 'videos';

const SKILL_VIEW_META: Record<
  string,
  { name: string; title: string; icon: string }
> = {
  briefing: { name: 'briefing', title: '주간 브리핑', icon: '🏋️' },
  meal: { name: 'meal', title: '오늘 식단', icon: '🍽️' },
  recovery: { name: 'recovery', title: '액티브 회복', icon: '🧘' },
  countdown: { name: 'countdown', title: '대회 카운트다운', icon: '🗓️' },
  videos: { name: 'videos', title: '추천 영상', icon: '🎬' },
};

export function App() {
  const [screen, setScreen] = useState<Screen>('home');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const user = WebApp.initDataUnsafe.user;
  const userName = user?.first_name ?? 'Member';

  useEffect(() => {
    fetchMe()
      .then((r) => setMe(r))
      .finally(() => setLoading(false));
  }, []);

  if (screen === 'rest_timer') return <RestTimer onBack={() => setScreen('home')} />;
  if (screen === 'tracker') return <TwelveWeekTracker onBack={() => setScreen('home')} me={me} />;
  if (screen === 'pr') return <PRChallenge onBack={() => setScreen('home')} />;
  if (screen === 'form') return <FormGuide onBack={() => setScreen('home')} />;
  if (screen === 'injury') return <InjuryRecovery onBack={() => setScreen('home')} />;

  // 텍스트 skill 5종 — generic SkillView (backend `/api/skill/:name` fetch)
  const meta = SKILL_VIEW_META[screen];
  if (meta) {
    return (
      <SkillView
        skillName={meta.name}
        title={meta.title}
        icon={meta.icon}
        onBack={() => setScreen('home')}
      />
    );
  }

  return (
    <div className="app">
      <div className="app-header">
        <h1>안녕하세요, {userName}님</h1>
        <p>
          {loading
            ? '프로필 로드 중…'
            : me?.ok
              ? `${me.fitnessProfile?.goal ?? 'general'} 모드 · Phase ${me.fitnessProfile?.currentPhase ?? 1}/4`
              : '오늘도 함께 운동 시작해볼까요'}
        </p>
      </div>

      <div className="skill-list">
        <button className="skill-card" onClick={() => setScreen('rest_timer')}>
          <span className="icon">⏱️</span>
          <div className="meta">
            <h3>쉬는 시간 타이머</h3>
            <p>세트 사이 휴식 정확히 측정</p>
          </div>
        </button>

        <button className="skill-card" onClick={() => setScreen('tracker')}>
          <span className="icon">📊</span>
          <div className="meta">
            <h3>12주 트래커</h3>
            <p>현재 Phase 진행도 + 캘린더</p>
          </div>
        </button>

        <button className="skill-card" onClick={() => setScreen('pr')}>
          <span className="icon">🏆</span>
          <div className="meta">
            <h3>PR 도전</h3>
            <p>종목별 기록 + 그래프</p>
          </div>
        </button>

        <button className="skill-card" onClick={() => setScreen('form')}>
          <span className="icon">🎬</span>
          <div className="meta">
            <h3>폼 가이드</h3>
            <p>YouTube 구간 반복 + 체크포인트</p>
          </div>
        </button>

        <button className="skill-card" onClick={() => setScreen('injury')}>
          <span className="icon">🩹</span>
          <div className="meta">
            <h3>부상 회복 4단계</h3>
            <p>Phase별 progress + 체크리스트</p>
          </div>
        </button>

        <h3 style={{ marginTop: 24, marginBottom: 8, fontSize: 14, color: 'var(--tg-hint)' }}>
          텍스트 스킬 (봇에서도 제공)
        </h3>

        <button className="skill-card" onClick={() => setScreen('briefing')}>
          <span className="icon">🏋️</span>
          <div className="meta">
            <h3>주간 브리핑</h3>
            <p>이번주 운동 plan</p>
          </div>
        </button>

        <button className="skill-card" onClick={() => setScreen('meal')}>
          <span className="icon">🍽️</span>
          <div className="meta">
            <h3>오늘 식단</h3>
            <p>목표 기반 단백질·탄수·수분</p>
          </div>
        </button>

        <button className="skill-card" onClick={() => setScreen('recovery')}>
          <span className="icon">🧘</span>
          <div className="meta">
            <h3>액티브 회복</h3>
            <p>level별 회복 루틴</p>
          </div>
        </button>

        <button className="skill-card" onClick={() => setScreen('countdown')}>
          <span className="icon">🗓️</span>
          <div className="meta">
            <h3>대회 카운트다운</h3>
            <p>등록한 대회 D-day</p>
          </div>
        </button>

        <button className="skill-card" onClick={() => setScreen('videos')}>
          <span className="icon">🎬</span>
          <div className="meta">
            <h3>추천 영상</h3>
            <p>profile 기반 큐레이션</p>
          </div>
        </button>
      </div>

      <p style={{
        marginTop: 32,
        padding: 12,
        fontSize: 11,
        color: 'var(--tg-hint)',
        textAlign: 'center',
        lineHeight: 1.5,
      }}>
        🤖 AI 보조 코칭 — 의료 진단·치료 권고 X<br />
        부상·통증 지속 시 의사 상담 권장
      </p>
    </div>
  );
}
